/**
 * SuggestionQualityComparison (Plan v9 §4.2 prototype rework).
 *
 * Renders a 2-column "Day 1 (vanilla LLM) vs Today" comparison table.
 * Each row carries a metric name, a Day 1 value, a Today value, and a
 * direction tag that drives the color-coded delta cell:
 *   - improved  -> status-active-fg on status-active-bg ("better")
 *   - regressed -> warning-strong-fg on warning-strong-bg ("worse")
 *   - unchanged -> text-tertiary (neutral)
 *
 * Data source: `MetricsSnapshot.suggestionQualityRows` and
 * `eventsIndexedCount`. Demo Mode supplies the canned baseline; Live
 * Mode populates from App Insights queries.
 *
 * HC2 token-only: every color, spacing, radius value resolves through
 * Signal DS tokens. No raw hex.
 * HC5: zero em / en dashes anywhere.
 * HC6 a11y: semantic `<table>` with caption + scoped headers. Each
 *   row's direction tag is announced via the trailing cell text (e.g.
 *   "improved") so SR users get the comparison sense without color.
 */
import type { SuggestionQualityRow } from '../../services/metrics/types';

export interface SuggestionQualityComparisonProps {
  /** Per-metric comparison rows (Day 1 vs Today). */
  rows: ReadonlyArray<SuggestionQualityRow>;
  /** Total events indexed; surfaced in the "Today" column header. */
  eventsIndexedCount: number;
  /** Optional className for the outer wrapper. */
  className?: string;
}

const DIRECTION_LABEL: Record<SuggestionQualityRow['direction'], string> = {
  improved: 'Improved',
  regressed: 'Regressed',
  unchanged: 'Unchanged',
};

const DIRECTION_CLASSES: Record<SuggestionQualityRow['direction'], string> = {
  improved:
    'inline-flex items-center gap-gutter-sm rounded-signal-pill bg-status-active-bg px-gutter-sm py-1 text-signal-xs font-semibold text-status-active-fg',
  regressed:
    'inline-flex items-center gap-gutter-sm rounded-signal-pill bg-warning-strong-bg px-gutter-sm py-1 text-signal-xs font-semibold text-warning-strong-fg',
  unchanged:
    'inline-flex items-center gap-gutter-sm rounded-signal-pill bg-surface-muted px-gutter-sm py-1 text-signal-xs font-semibold text-text-tertiary',
};

function formatCount(n: number): string {
  return Number.isFinite(n) ? n.toLocaleString('en-US') : '0';
}

export function SuggestionQualityComparison({
  rows,
  eventsIndexedCount,
  className,
}: SuggestionQualityComparisonProps) {
  const composed = [
    'flex flex-col gap-gutter-md rounded-signal-lg border border-surface-border bg-surface-base p-gutter-lg shadow-signal-card',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <section
      aria-labelledby="suggestion-quality-comparison-title"
      data-testid="suggestion-quality-comparison"
      className={composed}
    >
      <header className="flex flex-col gap-gutter-sm">
        <h3
          id="suggestion-quality-comparison-title"
          className="text-signal-lg font-semibold text-text-primary"
        >
          Suggestion Quality Comparison
        </h3>
        <p className="text-signal-sm text-text-secondary">
          Vanilla baseline vs current state. Improvements are the
          payoff of the learning loop.
        </p>
      </header>

      <table
        data-testid="suggestion-quality-comparison-table"
        className="w-full text-left text-signal-sm"
      >
        <caption className="sr-only">
          Suggestion quality comparison: Day 1 vanilla baseline versus
          today after {formatCount(eventsIndexedCount)} events indexed.
        </caption>
        <thead>
          <tr className="border-b border-surface-border text-text-tertiary">
            <th scope="col" className="py-gutter-sm pr-gutter-md text-signal-xs uppercase tracking-wide">
              Metric
            </th>
            <th scope="col" className="py-gutter-sm pr-gutter-md text-signal-xs uppercase tracking-wide">
              Day 1 (vanilla LLM)
            </th>
            <th scope="col" className="py-gutter-sm pr-gutter-md text-signal-xs uppercase tracking-wide">
              Today ({formatCount(eventsIndexedCount)} events indexed)
            </th>
            <th scope="col" className="py-gutter-sm text-signal-xs uppercase tracking-wide">
              Direction
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.metric}
              data-testid={`suggestion-quality-row-${row.metric.replace(/\s+/g, '-').toLowerCase()}`}
              data-direction={row.direction}
              className="border-b border-surface-border last:border-b-0"
            >
              <th scope="row" className="py-gutter-sm pr-gutter-md font-medium text-text-primary">
                {row.metric}
              </th>
              <td className="py-gutter-sm pr-gutter-md font-mono text-text-secondary">
                {row.day1}
              </td>
              <td className="py-gutter-sm pr-gutter-md font-mono font-semibold text-text-primary">
                {row.today}
              </td>
              <td className="py-gutter-sm">
                <span
                  className={DIRECTION_CLASSES[row.direction]}
                  data-testid={`suggestion-quality-direction-${row.metric.replace(/\s+/g, '-').toLowerCase()}`}
                >
                  {DIRECTION_LABEL[row.direction]}
                </span>
              </td>
            </tr>
          ))}
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={4}
                data-testid="suggestion-quality-comparison-empty"
                className="py-gutter-md text-signal-sm text-text-tertiary"
              >
                No comparison data yet. Rows will populate as events are
                indexed.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </section>
  );
}
